import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import API_URL from '../config';

const UserMenu = () => {
  const { user, logout, createCustomerPortal, subscriptionStatus } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastVideo, setLastVideo] = useState(null);

  // Expose a callback prop to parent to trigger last video view
  // (set by App.js)
  const { onViewLastVideo } = typeof window !== "undefined" && window.BrainRotAIUserMenuProps
    ? window.BrainRotAIUserMenuProps
    : {};

  useEffect(() => {
    const fetchLastVideo = async () => {
      if (isOpen && user) {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API_URL}/api/last-video`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setLastVideo(res.data);
        } catch (e) {
          setLastVideo(null);
        }
      }
    };
    fetchLastVideo();
  }, [isOpen, user]);

  const handleManageBilling = async () => {
    setLoading(true);
    try {
      const portalUrl = await createCustomerPortal();
      window.open(portalUrl, '_blank');
    } catch (error) {
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = () => {
    switch (subscriptionStatus) {
      case 'active':
        return 'Pro';
      case 'trialing':
        return 'Trial';
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      default:
        return 'Free';
    }
  };

  const getStatusColor = () => {
    switch (subscriptionStatus) {
      case 'active':
        return '#10b981';
      case 'trialing':
        return '#6366f1';
      case 'past_due':
        return '#ef4444';
      case 'canceled':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  const getTrialTimeRemaining = () => {
    if (subscriptionStatus === 'trialing' && user?.trial_ends_at) {
      const trialEnd = new Date(user.trial_ends_at);
      const now = new Date();
      const timeLeft = trialEnd - now;
      
      if (timeLeft > 0) {
        const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
        const minutesLeft = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
        return `${hoursLeft}h ${minutesLeft}m left`;
      }
    }
    return null;
  };

  if (!user) return null;

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="user-menu-button"
      >
        <div className="user-info">
          <span className="user-email">{user.email}</span>
          <span 
            className="user-status"
            style={{ 
              color: getStatusColor(),
              backgroundColor: `${getStatusColor()}20`,
              borderColor: `${getStatusColor()}40`
            }}
          >
            {getStatusDisplay()}
          </span>
        </div>
      </button>

      {isOpen && (
        <div className="user-dropdown">
          <div className="dropdown-content">
            <div className="dropdown-header">
              <div className="dropdown-email">{user.email}</div>
              <div className="dropdown-status-row">
                <span 
                  className="dropdown-status"
                  style={{ color: getStatusColor() }}
                >
                  {getStatusDisplay()}
                </span>
                {getTrialTimeRemaining() && (
                  <span className="trial-time">{getTrialTimeRemaining()}</span>
                )}
              </div>
            </div>
            {/* Last Video Button/Row */}
            {lastVideo && lastVideo.video_url ? (
              <button
                className="dropdown-button green-text"
                onClick={() => onViewLastVideo && onViewLastVideo(lastVideo.video_url)}
              >
                View Last Video
              </button>
            ) : (
              <button
                className="dropdown-button"
                style={{
                  width: "100%",
                  textAlign: "left",
                  color: "#888",
                  fontWeight: 500,
                  margin: 0,
                  marginBottom: 4,
                  background: "none",
                  border: "none",
                  borderRadius: 0,
                  padding: "8px 12px",
                  cursor: "not-allowed",
                  opacity: 0.6
                }}
                disabled
              >
                View Last Video
              </button>
            )}
            
            {subscriptionStatus !== 'none' && (
              <button
                onClick={handleManageBilling}
                disabled={loading}
                className="dropdown-button"
              >
                {loading ? 'Loading...' : 'Manage Billing'}
              </button>
            )}
            
            <button
              onClick={logout}
              className="dropdown-button logout"
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
